<template>
    <div class="account-selector" v-click-outside="closeDropdown" @click="openDropdown">
        <template v-if="!selectedAccount">
            <ui-input
                id="name"
                v-model="email"
                name="name"
                type="email"
                placeholder="email@example.com"
                :label="$t('Search by email')"
                :debounce="500"
                :field="v$.email"
                :show-optional-label="false"
                @input="searchEmail"
            >
                <template #prepend>
                    <span class="fas fa-search"></span>
                </template>
            </ui-input>
            <div v-if="showDropdown" class="account-selector__dropdown">
                <div v-if="loading" class="account-selector__account">
                    <b-skeleton type="avatar"></b-skeleton>
                    <div class="flex-grow-1">
                        <b-skeleton class="w-50" />
                        <b-skeleton class="w-50" />
                    </div>
                </div>
                <template v-else>
                    <div v-for="account in accounts" :key="account.email" class="account-selector__account" @click.stop="selectAccount(account)">
                        <div>
                            <avatar :name="account.first_name" :lastName="account.last_name" size="xs" />
                        </div>
                        <div class="account-selector__account-content">
                            <h6 class="mb-0">
                                {{ `${account.first_name} ${account.last_name}` }}
                            </h6>
                            <p class="mb-0 text-muted">
                                {{ account.email }}
                            </p>
                        </div>
                    </div>
                    <div v-if="!accounts.length" class="account-selector__account" @click.stop="selectAccount(email)">
                        <div>
                            <div class="account-selector__account-icon">
                                <span class="fas fa-envelope"></span>
                            </div>
                        </div>
                        <div class="account-selector__account-content">
                            <h6 class="mb-0">
                                {{ email }}
                            </h6>
                            <p class="mb-0 text-muted">
                                {{ $t('Email not registered on the platform.') }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
        </template>
        <div v-else class="scope-form__account mt-3">
            <div>
                <avatar v-if="selectedAccount.first_name" :name="selectedAccount.first_name" :lastName="selectedAccount.last_name" size="xs" />
                <div v-else class="scope-form__account-icon">
                    <span class="fas fa-envelope"></span>
                </div>
            </div>
            <div class="scope-form__account-content">
                <h6 class="mb-0">
                    {{ selectedAccount.first_name ? `${selectedAccount.first_name} ${selectedAccount.last_name}` : selectedAccount.email }}
                </h6>
                <p class="mb-0 text-muted">
                    <template v-if="selectedAccount.first_name">
                        {{ selectedAccount.email }}
                    </template>
                    <template v-else>
                        <span class="fas fa-info-circle"></span>
                        {{ $t('Email not registered on the platform.') }}
                    </template>
                </p>
            </div>
            <button v-if="clearable" class="scope-form__action scope-form__action--delete" @click="removeAccount">
                <span class="fas fa-trash"></span>
            </button>
        </div>
    </div>    
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { helpers, email, required } from '@vuelidate/validators';

import Avatar from '@/components/ui/Avatar.vue';
import UiInput from '@/components/ui/inputs/Input.vue';

export default {
    name: 'AccountSelector',
    components: { Avatar, UiInput },
    props: {
        clearable: {
            default: true,
            required: false,
            type: Boolean,
        },
        value: {
            type: [Object, null],
            default: null,
            required: false,
        },
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            accounts: [],
            email: '',
            loading: false,
            selectedAccount: null,
            showDropdown: false,
        };
    },
    computed: {
        rules() {
            const rules = {}

            if (!this.selectedAccount) {
                rules.email = {
                    required: helpers.withMessage(this.$t('It is necessary to select an account'), required),
                    email: helpers.withMessage(this.$t('Email invalid'), email),
                    requiredAccount: helpers.withMessage(this.$t('It is necessary to select an account'), () => {
                        return this.showDropdown;
                    }),
                };
            }

            return rules;
        },
    },
    validations() {
        return this.rules;
    },
    mounted() {
        if (this.value) {
            this.selectedAccount = this.value;
        }
    },
    methods: {
        closeDropdown() {
            this.showDropdown = false;
        },
        openDropdown() {
            if (this.email && !this.v$.email.email.$invalid) {
                this.showDropdown = true;
            }
        },
        selectAccount(account) {
            let formatted_account = account;

            if (typeof account === 'string') {
                formatted_account = { email: account };
            }

            this.selectedAccount = formatted_account;
            this.$emit('input', formatted_account);
            this.closeDropdown();
            this.email = '';
            this.v$.$reset();
        },
        removeAccount() {
            this.selectedAccount = null;
            this.$emit('input', null);
        },
        async searchEmail(email) {
            this.v$.$touch();

            if(!email || this.v$.email.email.$invalid) {
                this.showDropdown = false;
                return;
            }

            try {
                this.loading = true;
                this.showDropdown = true;

                const { data } = await this.axiosAccount.get('/api/accounts/search', { params: { email } });

                this.accounts = data;
            } catch(error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        }
    },
};
</script>

<style lang="scss">
.account-selector {
    position: relative;

    &__dropdown {
        background-color: $white;
        border-radius: 8px;
        box-shadow: 0 8px 16px rgba(#000000, 0.15);
        left: 0;
        max-height: 250px;
        overflow-y: auto;
        position: absolute;
        top: calc(100% + 2px);
        width: 100%;
        z-index: 99;
    }

    &__account {
        align-items: center;
        border-radius: 8px;
        cursor: pointer;
        display: flex;
        gap: 8px;
        padding: 16px;

        &:hover {
            background-color: #f6f6f6;
        }
    }

    &__account-icon {
        align-items: center;
        background-color: $accounts-primary;
        border-radius: 100%;
        color: $white;
        display: flex;
        font-size: 1rem;
        height: 40px;
        justify-content: center;
        width: 40px;
    }

    &__account-content {
        width: 100%
    }
}
</style>
